/**
 * In order to slide to items/groups from outside, you can trigger events
 * on the ".slider" wrap:
 * 	- slide-to-item (passing { itemIndex: number, noAnim?: boolean })
 * 	- slide-to-prev-item
 * 	- slide-to-next-item
 * 	- slide-to-group (passing { groupIndex: number, noAnim?: boolean })
 * 	- slide-to-prev-group
 * 	- slide-to-next-group
 *
 * You can listen for the ".slider" wrap's "change" event, which will contain
 * the following data:
 * 	- groupIndex
 *  - itemIndex
 * 	- maxIndex
 * 	- numGroups
 * 	- prevAllowed (bool)
 * 	- nextAllowed (bool)
 * 
 * If for some reason you need to trigger a change event (with the data object),
 * you can trigger the ".slider" wrap's "force-change" event.
 * 
 */

import { throttle, ceil, floor, each, min, max, range } from "lodash";
import {
	getWaitValueFromWrapAttr,
	getItemsPerGroupDataFromWrapAttr,
	getItemsPerGroup,
	findHighestItemsPerGroupValue,
	highlightGroupControl,
	getAutoSlideCountValueFromWrapAttr,
} from "./slider-helpers";
import { zeroPadNumber } from "./utils/misc";
import * as Hammer from "hammerjs";

declare const jQuery: any;
const $ = jQuery;
const $window = $(window);

export interface SliderSettings
{
	disableSwipe?: boolean;
	returnToStart?: boolean;
	returnToEnd?: boolean;
	chevronsByItem?: boolean;
	infinite?: boolean; // Experimental. Use inadvisable.
}

const config = Object.freeze({
	dfItemsPerGroup: [
		{ minWindowWidth: 0, nItems: 1 },
	]
});

export function initFiniteEqualWidthSliders(): void
{
	$(".finite-equal-width-slider").each(function () {
		const $wrap = $(this);
		initSlider($wrap);
	});
}

export function initSlider($wrap, settings?: SliderSettings): void
{
	settings = settings || {};

	if ((settings.disableSwipe === undefined) && $wrap.hasClass(`disable-swipe`))
	{
		settings.disableSwipe = true;
	}

	if ((settings.returnToStart === undefined) && $wrap.hasClass(`return-to-start`))
	{
		settings.returnToStart = true;
	}

	if ((settings.returnToEnd === undefined) && $wrap.hasClass(`return-to-end`))
	{
		settings.returnToEnd = true;
	}

	if ((settings.chevronsByItem === undefined) && $wrap.is(`[data-chevrons-by-item]`))
	{
		settings.chevronsByItem = true;
	}

	const $slider = $wrap.find(".slider-items-slider");

	$slider.children().not(".slider-item").remove(); // sometimes empty <p> strays in
	
	const autoSlideWait: number|undefined = getWaitValueFromWrapAttr($wrap);
	const autoSlideCount: number|"group"|undefined = autoSlideWait ? getAutoSlideCountValueFromWrapAttr($wrap) : undefined;
	let autoSlideCancelled: boolean = false;
	const itemsPerGroupSettings = getItemsPerGroupDataFromWrapAttr($wrap, config.dfItemsPerGroup);
	initSliderLayout($slider, $window, itemsPerGroupSettings);
	
	let itemIndex = 0;
	let autoslideTimer: any;

	function getMaxIndex(): number
	{
		return calcMaxIndex($slider, $window, itemsPerGroupSettings);
	}

	function getNumGroups(): number
	{
		return ceil($slider.children().length / getItemsPerGroup($window, itemsPerGroupSettings));
	}
	
	function goTo(newIndex: number, forceChangeEvent: boolean = false, noAnim: boolean = false): void
	{
		clearTimeout(autoslideTimer);
		const maxIndex: number = getMaxIndex();
		newIndex = min([max([0, newIndex]), maxIndex]);
		slideToItem($slider, newIndex, noAnim);
		const numGroups: number = getNumGroups();
		const groupIndex: number = getGroupIndex(newIndex, maxIndex, numGroups, $window, itemsPerGroupSettings);
		highlightGroupControl($wrap, groupIndex);
		setPrevNextDisabled($wrap, newIndex, maxIndex);
		const changed: boolean = itemIndex !== newIndex;
		itemIndex = newIndex;
		if (changed || forceChangeEvent) {
			$slider.trigger("change", {
				groupIndex,
				itemIndex,
				maxIndex,
				numGroups,
				prevAllowed: itemIndex > 0,
				nextAllowed: itemIndex < maxIndex
			});
		}
		if (autoSlideWait && !autoSlideCancelled)
		{
			nextAutoSlide();
		}
	}

	function nextAutoSlide(): void
	{
		clearTimeout(autoslideTimer);
		if (autoSlideCancelled)
		{
			return;
		}
		autoslideTimer = setTimeout(() => {
			if (itemIndex < getMaxIndex())
			{
				if (autoSlideCount === "group") {
					nextGroup();
				} else if (autoSlideCount) {
					const newIndex: number = itemIndex + autoSlideCount;
					goTo(newIndex <= getMaxIndex() ? newIndex: 0);
				} else {
					nextItem();
				}
			}
			else
			{
				goTo(0);
			}
			nextAutoSlide();
		}, autoSlideWait);
	}

	function goToGroup(groupIndex: number, noAnim: boolean = false): void
	{
		goTo(groupIndex * getItemsPerGroup($window, itemsPerGroupSettings), false, noAnim);
	}

	const prevItem = () => {
		if (settings.returnToEnd && (itemIndex <= 0))
		{
			goTo(getMaxIndex());
		}
		else
		{
			goTo(itemIndex - 1);
		}
	};
	const nextItem = () => {
		if (settings.returnToStart && (itemIndex >= getMaxIndex()))
		{
			goTo(0);
		}
		else
		{
			goTo(itemIndex + 1);
		}
	};
	const prevGroup = () => goTo(itemIndex - getItemsPerGroup($window, itemsPerGroupSettings));
	const nextGroup = () => {
		if (settings.returnToStart && (itemIndex >= getMaxIndex()))
		{
			goTo(0);
		}
		else
		{
			goTo(itemIndex + getItemsPerGroup($window, itemsPerGroupSettings));
		}
	};

	const getCurrentGroupIndex = (): number => {
		const maxIndex: number = getMaxIndex();
		const numGroups: number = getNumGroups();
		return getGroupIndex(itemIndex, maxIndex, numGroups, $window, itemsPerGroupSettings);
	};

	initPrevAndNextGroupButtons($wrap, settings.chevronsByItem ? prevItem : prevGroup, settings.chevronsByItem ? nextItem : nextGroup);
	initNavControls($wrap, $slider, $window, goToGroup, getCurrentGroupIndex, itemsPerGroupSettings);

	let setSwipeDisabled = (disabled: boolean) => {};
	if (!settings.disableSwipe)
	{
		setSwipeDisabled = initSwipe($wrap, prevGroup, nextGroup);
	}
	highlightGroupControl($wrap, 0);
	setPrevNextDisabled($wrap, 0, getMaxIndex());

	$window.on("resize", throttle(() => {
		goTo(itemIndex, false, true);
	}, 50));

	$wrap.on("disable-swipe", () => {
		setSwipeDisabled(true);
	}).on("enable-swipe", () => {
		setSwipeDisabled(false);
	});

	$wrap.addClass("show");

	$wrap.on("slide-to-item", (e, data) => {
		if (!data || data.itemIndex === undefined)
		{
			console.error("No itemIndex passed to slider slide-to-item handler");
			return;
		}
		goTo(data.itemIndex, false, !!data.noAnim);
	});

	$wrap.on("slide-to-prev-item", (e, data) => {
		prevItem();
	});

	$wrap.on("slide-to-next-item", (e, data) => {
		nextItem();
	});

	$wrap.on("slide-to-group", (e, data) => {
		if (data.groupIndex === undefined)
		{
			console.error("No groupIndex passed to slider slide-to-group handler");
			return;
		}
		goToGroup(data.groupIndex, !!data.noAnim);
	});

	$wrap.on("slide-to-prev-group", (e, data) => {
		prevGroup();
	});

	$wrap.on("slide-to-next-group", (e, data) => {
		nextGroup();
	});

	$wrap.on("force-change", (e, data) => {
		goTo(itemIndex, true);
	});

	if (autoSlideWait)
	{
		nextAutoSlide();
	}
}

function initSwipe($wrap, prevGroup: Function, nextGroup: Function): (disabled: boolean) => void
{
	let disabled: boolean = false;
	const wrap = $wrap[0];
	
	let hammer: any;

	const prevGroupWrapped = () => { prevGroup(); };
	const nextGroupWrapped = () => { nextGroup(); };

	const addOrRemoveHandlers = () => {
		if (disabled)
		{
			if (hammer)
			{
				hammer.off("swiperight", prevGroupWrapped);
				hammer.off("swipeleft", nextGroupWrapped);
				hammer.destroy();
			}
		}
		else
		{
			hammer = new Hammer(wrap, {});
			hammer.on("swiperight", prevGroupWrapped);
			hammer.on("swipeleft", nextGroupWrapped);
		}
	};

	addOrRemoveHandlers();
	
	return (isDisabled: boolean) => {
		if (disabled !== isDisabled)
		{
			disabled = isDisabled;
			addOrRemoveHandlers();
		}
	};
}

function getGroupIndex(itemIndex: number, maxItemIndex: number, numGroups: number, $window, itemsPerGroupSettings): number
{
	if (itemIndex >= maxItemIndex)
	{
		return numGroups - 1;
	}
	const groupIndex: number = floor(itemIndex / getItemsPerGroup($window, itemsPerGroupSettings));
	if (groupIndex === 0 && itemIndex > 0 && numGroups > 1)
	{
		return 1;
	}
	return groupIndex;
}

function initSliderLayout($slider, $window, itemsPerGroupSettings): void
{
	const fixLayout = () => setWidths($slider, $window, itemsPerGroupSettings);
	fixLayout();
	$window.on("load resize", throttle(fixLayout, 50));
}

function initPrevAndNextGroupButtons($wrap, prev: () => void, next: () => void): void
{
	$wrap.find(`[data-slider-ctrl-prev]`).on("click", function (ev) {
		prev();
		return false;
	});
	$wrap.find(`[data-slider-ctrl-next]`).on("click", function (ev) {
		next();
		return false;
	});
}

function setPrevNextDisabled($wrap, itemIndex: number, maxIndex: number): void
{
	const $prev = $wrap.find(`[data-slider-ctrl-prev]`).removeClass("disabled");
	const $next = $wrap.find(`[data-slider-ctrl-next]`).removeClass("disabled");
	const shouldDisablePrev: boolean = itemIndex == 0;
	const shouldDisableNext: boolean = itemIndex == maxIndex;
	if (shouldDisablePrev)
	{
		$prev.addClass("disabled");
	}
	if (shouldDisableNext)
	{
		$next.addClass("disabled");
	}
	if (shouldDisablePrev && shouldDisableNext)
	{
		$wrap.addClass("all-nav-disabled");
	}
	else
	{
		$wrap.removeClass("all-nav-disabled");
	}
}

function initNavControls(
	$wrap,
	$slider,
	$window,
	goToGroup: (groupIndex: number) => void,
	getCurrentGroupIndex: () => number,
	itemsPerGroupSettings): void
{
	initNavControlsForType("dot", $wrap, $slider, $window, goToGroup, getCurrentGroupIndex, itemsPerGroupSettings);
	initNavControlsForType("number", $wrap, $slider, $window, goToGroup, getCurrentGroupIndex, itemsPerGroupSettings);
}

// $sliderWrap - jQuery object for ".finite-equal-width-slider" div
// $appendToThis - if provided, the new controls wrapper div will be append to this
// Returns jQuery for new controls wrapper div
function createNavControls($sliderWrap, type?: "dot"|"number", $appendToThis?)
{
	type = type || "dot";
	const $slider = $sliderWrap.find(".slider-items-slider");
	const $wrap = $(`<div><div class="${type}-controls"><div></div></div></div>`);
	const itemsPerGroupSettings = getItemsPerGroupDataFromWrapAttr($sliderWrap, config.dfItemsPerGroup);
	const goToGroup = (groupIndex: number) => {
		$slider.trigger("slide-to-group", { groupIndex });
	};
	let groupIndex: number = 0;
	const getCurrentGroupIndex = (): number => groupIndex;
	const $ctrlWrap = initNavControlsForType(type, $wrap, $slider, $(window), goToGroup, getCurrentGroupIndex, itemsPerGroupSettings);
	if ($appendToThis)
	{
		$appendToThis.append($wrap.children());
	}
	$sliderWrap.on("change", function (event, data) {
		highlightGroupControl($ctrlWrap, data.groupIndex);
		groupIndex = data.groupIndex;
	});
	$ctrlWrap.on("nav-ctrl-reset", function (event, data) {
		if (!data || (data.groupIndex === undefined))
		{
			throw new Error(`No groupIndex on nav-ctrl-reset data`);
		}
		highlightGroupControl($ctrlWrap, data.groupIndex);
		groupIndex = data.groupIndex;
	});
	highlightGroupControl($ctrlWrap, groupIndex);
	return $ctrlWrap;
}

function initNavControlsForType(
	type: "dot"|"number",
	$wrap,
	$slider,
	$window,
	goToGroup: (groupIndex: number) => void,
	getCurrentGroupIndex: () => number,
	itemsPerGroupSettings)
{
	const $outerWrap = $wrap.find(`.${type}-controls`);
	const $ctrlWrap = $outerWrap.children();
	if ($ctrlWrap.length === 0)
	{
		return $outerWrap;
	}
	const isDot: boolean = (type === "dot");
	const shouldZeroPadNumber: boolean = (type === "number") && $wrap.hasClass("zero-pad-numbers");
	const reset = function reset(): void {
		const numItems: number = $slider.children().length;
		const itemsPerGroup: number = getItemsPerGroup($window, itemsPerGroupSettings);
		const numGroups: number = ceil(numItems / itemsPerGroup);
		if (numGroups < 2)
		{
			$ctrlWrap.css({ display: "none" });
			return;
		}
		$ctrlWrap.empty().css({ display: "" });
		each(range(0, numGroups), (groupIndex: number) => {
			const numStr: string = shouldZeroPadNumber ? zeroPadNumber(groupIndex + 1) : (groupIndex + 1).toString();
			const html: string = isDot ? `<div class="dot">` : `<div class="number">${numStr}</div>`;
			$(html).appendTo($ctrlWrap).on("click", function () {
				goToGroup(groupIndex);
				return false;
			});
		});
		const groupIndex: number = getCurrentGroupIndex();
		highlightGroupControl($outerWrap, groupIndex);
		$outerWrap.trigger("nav-ctrl-reset", { groupIndex });
	};
	reset();
	$window.on("resize orientationchange load", throttle(reset, 50, { trailing: true }));
	return $outerWrap;
}

function calcMaxIndex($slider, $window, itemsPerGroupSettings): number
{
	const numItems: number = $slider.children().length;
	const itemsPerGroup: number = getItemsPerGroup($window, itemsPerGroupSettings);
	const numGroups: number = ceil(numItems / itemsPerGroup);
	if (numGroups < 2)
	{
		return 0;
	}
	const firstIndexOfLastGroup: number = itemsPerGroup * (numGroups - 1);
	const remainder: number = (numItems % itemsPerGroup); // No. of items in last group or 0 if last group is full
	if (remainder === 0)
	{
		return firstIndexOfLastGroup;
	}
	const numItemsMissingInLastGroup: number = itemsPerGroup - remainder;
	return firstIndexOfLastGroup - numItemsMissingInLastGroup;
}

function setWidths($slider, $window, itemsPerGroupSettings): void
{
	const itemsPerGroup: number = getItemsPerGroup($window, itemsPerGroupSettings);
	const $items = $slider.children();
	const numItems: number = $items.length;
	const sliderWidthPercent: number = (100 / itemsPerGroup) * numItems;
	$slider.css({ width: sliderWidthPercent + "%" });
	const itemWidthPercent: number = 100 / numItems;
	$items.css({ width: itemWidthPercent + "%" });
}

function slideToItem($slider, itemIndex: number, noAnim: boolean = false): void
{
	if (!noAnim)
	{
		performSlideTransition($slider, itemIndex);
		return;
	}
	
	const $wrap = $slider.closest(".slider");
	const wrap = $wrap[0];
	
	if (!wrap)
	{
		console.error(`No .slider wrap found during slideToAnim`);
		return;
	}
	
	const clr = () => {
		clearTimeout(wrap._sliderNoAnimTimer);
	};
	
	const timeout = (fn: Function) => {
		clr();
		wrap._sliderNoAnimTimer = setTimeout(fn, 20);
	};
	
	$wrap.addClass("no-anim");

	timeout(() => {
		performSlideTransition($slider, itemIndex);
		timeout(() => {
			$wrap.removeClass("no-anim");
		});
	});
}

function performSlideTransition($slider, itemIndex: number): void
{
	const $items = $slider.children();
	const x: number = -itemIndex * $items.outerWidth();
	$slider.css({ transform: `translateX(${x}px)` });
}

function syncFiniteEqualWidthSliders($master, $slave): void
{
    if ($master.find(".slider-item").length !== $slave.find(".slider-item").length)
    {
        console.error("linkSliders failed because sliders have different no. items");
        return;
    }
    $master.on("change", function (event, data) {
        const itemIndex: number = data.itemIndex;
        $slave.trigger("slide-to-item", { itemIndex });
	});
	$slave.trigger("disable-swipe");
}

function initPrevNextButtonsForSlider($slider, $prev?, $next?): void
{
	if ($prev)
	{
		$prev.on("click", function () {
			$slider.trigger("slide-to-prev-group");
			return false;
		});
		$slider.on("change", function (event, data) {
			data.prevAllowed ? $prev.removeClass("disabled") : $prev.addClass("disabled");
		});
	}
	if ($next)
	{
		$next.on("click", function () {
			$slider.trigger("slide-to-next-group");
			return false;
		});
		$slider.on("change", function (event, data) {
			data.nextAllowed ? $next.removeClass("disabled") : $next.addClass("disabled");
		});
	}
	$slider.trigger("force-change");
}

if (!window["iw"])
{
	window["iw"] = {};
}
if (!window["iw"].sliders)
{
	window["iw"].sliders = {};
}
(function (sliders) {
	sliders.initFiniteEqualWidthSlider = initSlider;
	sliders.createNavControls = createNavControls;
	sliders.syncFiniteEqualWidthSliders = syncFiniteEqualWidthSliders;
	sliders.initPrevNextButtonsForSlider = initPrevNextButtonsForSlider;
})(window["iw"].sliders);
